import React, { useState } from 'react';
import { TESTIMONIAL_VOICES } from '../../data/content';
import { DoubleBezelCard } from '../common/DoubleBezelCard';
import { PixelGlyph } from '../common/PixelGlyph';
import { Quote, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';

export const EditorialVoices: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? TESTIMONIAL_VOICES.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === TESTIMONIAL_VOICES.length - 1 ? 0 : prev + 1));
  };

  const current = TESTIMONIAL_VOICES[currentIndex];

  return (
    <section
      id="voices"
      className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 max-w-6xl mx-auto z-10"
      aria-label="Editorial Testimonials"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 text-xs font-mono text-sunset-gold tracking-ultra uppercase mb-6">
        <PixelGlyph name="pulse-dot" size={10} />
        <span>RECOGNITION // CRITICAL ACCLAIM</span>
      </div>

      <DoubleBezelCard
        tilt={false}
        spotlight={true}
        innerClassName="p-6 sm:p-10 md:p-14 flex flex-col justify-between min-h-[320px] sm:min-h-[380px]"
      >
        <div>
          <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-sunset-gold/40 mb-5 sm:mb-8" />
          <blockquote className="font-display font-bold text-xl sm:text-2xl md:text-4xl text-cream-100 leading-snug tracking-tight mb-8 sm:mb-10 text-balance">
            "{current.quote}"
          </blockquote>
        </div>

        {/* Footer: Author Info & Controls */}
        <div className="pt-6 sm:pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-5 sm:gap-6">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-display font-bold text-base sm:text-lg text-cream-100">
                {current.author}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full border border-emerald-400/20">
                <CheckCircle2 className="w-3 h-3" />
                {current.verifiedStamp}
              </span>
            </div>
            <div className="text-xs font-mono text-cream-muted">
              {current.role} • <span className="text-cream-200">{current.org}</span> ({current.location})
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex items-center gap-3 self-end sm:self-auto">
            <button
              onClick={prevSlide}
              className="p-2.5 sm:p-3 rounded-full bg-espresso-850 hover:bg-espresso-800 border border-white/10 text-cream-muted hover:text-cream-100 transition-colors cursor-pointer"
              aria-label="Previous quote"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-mono text-cream-dim px-2">
              [{currentIndex + 1} / {TESTIMONIAL_VOICES.length}]
            </span>
            <button
              onClick={nextSlide}
              className="p-2.5 sm:p-3 rounded-full bg-espresso-850 hover:bg-espresso-800 border border-white/10 text-cream-muted hover:text-cream-100 transition-colors cursor-pointer"
              aria-label="Next quote"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </DoubleBezelCard>
    </section>
  );
};
